import React, { useEffect, useState, useCallback } from "react";
import { aiRegistry } from "./index";
import ChatWindow from "../../../ChatPage/Chatwindow";
import WelcomeScreen from "./WelcomeScreen";
import ReflectionScreen from "./ReflectionScreen";
import PromotionProcess from "./PromotionProcess";
import ChatLayout from "./ChatLayout";
import ProfileSidebar from "./ProfileSidebar";
import { progressService } from "../../../../services/progressService";
import ExitScreen from "./ExitScreen";
import VisibilityScreen from "./VisibilityScreen";
import AIConversation from "./AIConversation";


const AIContainer = ({
  userId,
  subsection,
  actions,
  onToggleImmersive,
  currentModule = null,
  onComplete,
  completed = false,
}) => {
  const [showChat, setShowChat] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [showIncompleteReminder, setShowIncompleteReminder] = useState(false);
  const [isMarkedComplete, setIsMarkedComplete] = useState(Boolean(completed));
  const [systemThinking, setSystemThinking] = useState(false);
  const [userTurn, setUserTurn] = useState(false);
  const slug = subsection?.subsection_url;


  useEffect(() => {
    setIsMarkedComplete(Boolean(completed));
    if (completed) {
      setShowIncompleteReminder(false);
    }
  }, [completed]);


  useEffect(() => {
    if (!subsection?.id) return;
    progressService.updateUserState(userId, subsection.id).catch(() => {});
  }, [userId, subsection?.id]);


  useEffect(() => {
    onToggleImmersive?.(showChat);
  }, [showChat, onToggleImmersive]);


  useEffect(() => {
    if (slug !== "general_reflection_agent") return;


    const handleOpenReflectionChat = () => {
      setShowChat(true);
    };


    window.addEventListener(
      "open-reflection-chat",
      handleOpenReflectionChat
    );


    return () => {
      window.removeEventListener(
        "open-reflection-chat",
        handleOpenReflectionChat
      );
    };
  }, [slug]);


  const handleStartAnalysis = () => {
    setShowChat(true);
    onToggleImmersive?.(true);
  };


  const handleExitChat = () => setShowExit(true);


  const handleStay = () => setShowExit(false);


  const handleExit = () => {
    setShowExit(false);
    setShowChat(false);
    if (!isMarkedComplete) {
      setShowIncompleteReminder(true);
    }
    onToggleImmersive?.(false);
  };


  const advanceToNextSection = useCallback(() => {
    if (typeof onComplete === "function") {
      onComplete();
      return;
    }


    if (actions) {
      actions.setPrimary?.({
        label: "Next Section",
        onClick: () => document.getElementById("next-lesson-btn")?.click(),
        visible: true,
      });
    }


    setTimeout(() => {
      document.getElementById("next-lesson-btn")?.click();
    }, 100);
  }, [actions, onComplete]);


  const handleMarkComplete = useCallback(() => {
    setShowExit(false);
    setShowChat(false);
    setShowIncompleteReminder(false);
    setIsMarkedComplete(true);
    onToggleImmersive?.(false);


    advanceToNextSection();


    progressService
      .saveProgress(
        userId,
        subsection.id,
        {
          type: "ai",
          payload: {},
        },
        "completed"
      )
      .catch((err) => {
        console.error("Error marking complete:", err);
        alert("Failed to mark section as complete. Please try again.");
      });
  }, [advanceToNextSection, onToggleImmersive, subsection?.id, userId]);


  useEffect(() => {
    if (!actions) return;
    actions.clearPrimary?.();
    return () => actions.clearPrimary?.();
  }, [actions]);


  const getWelcomeScreen = () => {
    switch (slug) {
      case "general_reflection_agent":
        return (
          <ReflectionScreen
            onStartAnalysis={handleStartAnalysis}
            subsection={subsection}
            onToggleImmersive={onToggleImmersive}
          />
        );
      case "unfairness_agent":
        return (
          <PromotionProcess
            onStartAnalysis={handleStartAnalysis}
            subsection={subsection}
          />
        );
      case "visibility_agent":
        return (
          <VisibilityScreen
            onStartAnalysis={handleStartAnalysis}
            subsection={subsection}
          />
        );
      default:
        return (
          <WelcomeScreen
            onStartAnalysis={handleStartAnalysis}
            subsection={subsection}
            subsectionUrl={slug}
          />
        );
    }
  };


  const useChatWindow = slug === "general_reflection_agent";
  const AgentComponent = (slug && aiRegistry[slug]) || aiRegistry.default;


  if (!AgentComponent) {
    return <p className="text-[#6B7280] italic">No matching AI agent found.</p>;
  }


  return (
    <div className="w-full h-[700px] flex flex-col overflow-hidden">
      {showIncompleteReminder && !showChat && !isMarkedComplete && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[#F1DCA7] bg-[#FFF8E6] px-4 py-3 text-sm text-[#5F4811]">
          <span>Chat is still in progress. Mark it complete when you're done.</span>
          <button
            type="button"
            onClick={() => setShowIncompleteReminder(false)}
            className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-[#5F4811] shadow-sm"
          >
            Dismiss
          </button>
        </div>
      )}
      {!showChat ? (
        getWelcomeScreen()
      ) : (
        <ChatLayout>
          {!useChatWindow && (
            <ProfileSidebar
              onExitChat={handleExitChat}
              systemThinking={systemThinking}
              userTurn={userTurn}
            />
          )}


          <div
            className={`flex-1 ${
              useChatWindow ? "max-w-full" : "max-w-[903px]"
            } h-[700px] bg-white rounded-[20px] shadow-sm overflow-hidden`}
          >
            <div className="w-full h-full flex flex-col">
              {useChatWindow ? (
                <ChatWindow
                  embedded={false}
                  userId={userId}
                  subsectionId={subsection?.id}
                  currentModule={currentModule}
                  onExitSession={() => {
                    setShowChat(false);
                    onToggleImmersive?.(false);
                  }}
                  onSessionComplete={handleMarkComplete}
                />
              ) : (
                <AIConversation
                  userId={userId}
                  subsection={subsection}
                  onSystemThinkingChange={setSystemThinking}
                  onUserTurnChange={setUserTurn}
                />
              )}
            </div>
          </div>


          {showExit && (
            <ExitScreen
              onStay={handleStay}
              onExit={handleExit}
              onComplete={handleMarkComplete}
              subsectionId={subsection.id}
              subsectionUrl={subsection.subsection_url}
            />
          )}
        </ChatLayout>
      )}
    </div>
  );
};


export default AIContainer;

